import React, { Component } from 'react';
import Header from '../../../Layout/Header'
import Sidebar from '../Sidebar'


class BlogItem extends Component {
	render() {
		return (
			<div>
				<Header
					bg="none"
					image={require('./image-1.jpg')}
					title={"Understanding Consumer Behaviour in Marketing and Design"}
				/>
				<div className="entry-content">
					
					<div className="container">
						<h2 className="blog-details">
								<div className="author-circle lauren"></div>
								<div className="line"></div>
								<div className="title">
									Written by <strong>Lauren</strong> on 07 May 2020  //  Posted in <strong>Marketing</strong>
								</div>
						</h2>
						
						<div className="row">
							<div className="col-lg-7">
								<div className="tagline">
									Understanding Consumer Behaviour in Marketing and Design
								</div>
								

<h3>The key to a successful website design or a highly converting marketing campaign is understanding your customers and how they think. </h3> 
<p>Consumer behaviour (also referred to as buying behaviour) refers to the principles in play when consumers are deciding what, when, how and where to purchase.</p> 
<p>A lot of these principles have been taught to us subliminally from birth (green = go, smile = happiness etc), while others have been cleverly marketed and structured over time (eg. Lamborghini = ultimate status symbol, more Facebook likes = a ‘better’ business). </p>
<br></br>

<h5>There are three primary factors that shape a buyer’s behaviour:</h5>

<ul class="with-icons">
<li><b>Personal Factors:</b> The individual’s age, gender, interests, hobbies, culture and profession. </li>
<li><b>Psychological Factors:</b> The individual’s attitude, opinions and perceptions to certain events, marketing strategies and theories.</li>
<li><b>Social Factors:</b> Friends and family influences on the person, social media influences, income, social class and other social situations that may affect their behaviour. </li>
</ul>

<hr></hr>
<img src={require('./image.jpg')} height="250" width="320" alt="Shopping Mall" className="align-left" align="left" /> 
<h5> For example - do you know why shopping centres don’t usually have windows or clocks? Seeing the sky and noticing the time plants the idea that people need to <i> rush to leave </i>(psychological factor).</h5>

<p>By not adding these items, mall designers can ensure people get lost in their experience and stay longer.</p>

<br></br>
<hr></hr>
<h2>Using Semiotics in Design</h2>
<h3>Semiotics refers to the use of signs, colour, wording and symbols in reflecting subconscious meaning.</h3> 

<p>Designers utilise all of this meaning to create visually appealing designs that provide the end user a deeper understanding and feel for the brand in question.</p>

<h5>For example, there is much more to colour selection than just what looks ‘good’. Certain colours unlock different emotions and meanings in our minds. </h5> 

<p>Think of the colour <b>red</b>. Now consider how many takeaway locations use this colour in their branding. </p>

<p>Though you might not be aware of it, our minds associate red with speed (convenience), energy (hunger), and passion (taste). This is why a lot of homeware stores sell red kitchen accessories - red stimulates the hunger drive in our brains, resulting in more cooking, leading to more equipment needed as your kitchen empire expands.</p> 

<p>The use of red in relation to hunger also explains why you subconsciously buy or make more food than you actually need - your hunger signals are in hyperdrive!</p>

<p>You may not realise your mind considers the factors associated with this colour, but designers and marketers do. </p>

<p>We consider the role of colour in deciding complex buying behaviour when designing <a href="https://www.moshi.com.au/whatwedo/brand-and-logo-design"> logos</a>, <a href="https://www.moshi.com.au/whatwedo/website-development"> websites</a>, <a href="https://www.moshi.com.au/whatwedo/graphic-design"> branding material</a>… everything really! But of course, different countries and cultures associate colour differently.</p>

<p>For example, the colour white is used heavily in marketing wedding/bridal businesses in Australia, as we associate white with fresh starts, new beginnings, purity, and wealth. However, in China, white is worn to funerals to show gratitude and respect for the individual, and red is worn at weddings for luck.</p>

<p>But colour is only a small part of consumer behaviour.</p>  <br></br>
 
<h5>The words you choose to use for your brand, and all associated copy also affects how your consumers behave, and which customers you attract. </h5>

<p>Using adjectives like 'comfort', 'love', 'snuggly', 'home', and 'warm' makes you want to get the know the brand. These words are <i>typically</i> associated with families, and strongly appeal to women (particularly Mums).</p>
<p>Harder adjectives such as 'strong', 'push', 'house', and 'now' <i>typically</i> appeal to more masculine audiences. Understanding how your consumers react to certain words affects your whole marketing machine. </p>

<p>The same applies to your <a href="https://www.moshi.com.au/blog/establishing-a-voice-for-your-brand"> brand voice</a> as a whole. What meaning are you trying to convey? Are you a warm, friendly voice, attracting like minded people? Are you a scientific, calculated voice, attracting audiences who want to learn?</p>
 
<p>Understanding what your customer wants to hear is a very important part of maximising your appeal and marketing efficiently.</p>

<br></br>
<hr></hr>
<h2>Understanding (and Marketing to) Consumers:</h2>

<h3>The most accurate way to gain a deep understanding of your customers and their complex buying behaviour is to ensure you’re collecting data rich information through <a href="https://www.moshi.com.au/whatwedo/google-advertising">Google Analytics</a> and <a href="https://www.moshi.com.au/whatwedo/social-media-marketing"> Facebook Pixels </a>.  </h3> 

<p><b>Google Analytics:</b> This gives us great insights as to the age range, gender, and location of your users, as well as showing which pages they went to, where their journey stopped, and how long was spent on each page. These factors show us where you can improve your customer experience, as well as who you should be allocating spend to. </p>
<p><b>Facebook Pixel:</b> This little snippet of code also shows us the age range, gender, and location of your users, as well as which campaigns generated conversions, and where we should best invest your spend.</p>

<p><b>Google Keyword Planner:</b> This tool allows insights into relevant keywords and search terms for your industry and business. By knowing what people are searching for, we can add specific keywords and search terms to your Header tags, meta-descriptions and content to provide a rich and relevant experience for your user.</p> 

<p><b>Heatmapping: </b>An in depth look at where your users are clicking, looking, and where their cursors are hovered for the longest.</p>

<p>Other response capturing tools such as <b> focus groups, surveys, reviews and social media posts</b> also give marketers great insights into what customers were influenced by when they did or didn’t purchase a product.</p> 

<h4>By understanding the WHY, we can create strong, more effective marketing  campaigns that eliminate the WHY NOT’s, and make the most of your marketing budget.</h4>
<img src={require('./image-2.jpg')} alt="Analysing your audience" height="300" width="650" className="align-centre" align="centre" />

<br></br>
<hr></hr>
<h2>Applying Complex Consumer Behaviour to Web Design:</h2>
<h3> A website that is designed around consumer and buyer behaviour is already going to outperform one that hasn’t considered their audience.</h3>

<p>Once we know the demographics, interests, social behaviour (do they use social media, do they value their friend’s opinions on products etc) and values of a business’s key consumers, we can design to appeal to that exact person.</p>

<p> Psychological factors again play an important role in the design of a web site. We find that women tend to like organic, rounder shapes, while men are attracted to sharp, geometric elements. Warmer colours and sofer tones play to the homely, maternal side of the brain, while colours such as black, greys and reds appeal to the more masculine, no-nonsense users.</p>

<p> Companies can use these psychological factors to create a <i>need</i> for items, simply by suggesting cross-sells or upsells alongside their primary item. Have you ever been on a site, and had a ‘Suggested Item’ appear alongside? That’s a successful website design, doing a great job at suggesting an add on based on the category of your product view. </p>

<p>Even something as simple as which menu style you choose affects consumer behaviour. <a href="https://www.goodui.org/evidence/test017">James Foster of Exisweb</a> (an expert on user interface) found that icons labelled with the word “Menu” (as opposed to the standard hamburger menu) showed an almost 20% increase in clicks.</p>

<p> This is due to icon recognisability, due to personal factors (such as age, culture), as well as social (do they use apps often, are they familiar with mobile sites?).</p> 

<img src={require('./image-4.png')} alt="Hamburger vs Menu" className="align-centre" align="centre" />
<p>Physical factors affect the functionality of a website design as well. As the below graphic shows, mobile adaptability is a huge consideration - if a menu is placed in the top left of the screen for example, it can be a pain to reach. This is why menus in mobile design are most often placed in the top right.</p>

<img src={require('./image-3.png')} alt="Menu Style" height="300" width="650" className="align-centre" align="centre" />

<br></br>
<hr></hr>
<h2>Data Based, Results Driven</h2>
<h3>At Moshi, our philosophy is to use the data gathered from your customer’s behaviours to drive our designs in graphics and websites, and to create effective, optimised marketing strategies catered exactly to your audience.</h3> 
<p>The science and understanding behind what we do is what creates the Moshi Moshi difference.</p>

<p>Now that you know a little more behind what drives colour, wording and design choices, I challenge you to think hard about your favourite brand. Which principles have they used to assist your purchase decision?</p>




							</div>
							<div className="col-lg-1"></div>
							<div className="col-lg-4">
								<Sidebar />
							</div>
						</div>
					</div>
				</div>

			</div>
		);
	}
}

export default BlogItem;


// WEBPACK FOOTER //
// ./src/Pages/Blog/ConsumerBehaviour/index.js